import { useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import { Auth } from "aws-amplify";

import { useSelector } from "react-redux";
import {
  resetUserLoginErrors,
  setCognitoUser,
  setIsLoggedIn,
  setIsLogout,
  setIsMandatoryDataFetched,
  setUserLoginErrors
} from "../../state/ducks";
import { dispatch, getState } from "../../state/store";
import { selectChallengedUser, selectCognitoUser } from "../../state/selectors";

import {
  DataFetching,
  EnterMfa,
  ForgotPassword,
  ResetPassword,
  SetupNewPassword,
  SimpleLogin
} from "../../containers";

import {
  appUserService,
  authService,
  log,
  notificationService,
  t,
  awsAmplifyService
} from "../../services";
import { awsAuthChallengeTypes, awsAuthErrorTypes, strCode } from "../../consts";
import { withSection } from "../../hoc";
import { useFetchMandatoryData } from "../../apiHooks";

import { TutorialModalTemplate } from "../../templates";
import { MfaSetupTutorial, PageContainer, Paragraph2 } from "../../components";
import { CognitoInvalidParameters } from "../../errors";

const MAX_LOGIN_ATTEMPTS = 6;
const ERROR_THRESHOLD = 1000 * 60;
const ACCOUNT_LOCK_TIMEOUT = 5 * 60 * 1000;
const ACCOUNT_LOCK_LAMBDA_ERROR_MESSAGE = "USER_IS_LOCKED_OUT";

const COGNITO_HOSTED_UI = process.env.REACT_APP_COGNITO_HOSTED_UI;

const LoginPage = () => {
  const [isLogin, setIsLogIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isMandatoryDataFetching, setIsMandatoryDataFetching] = useState(false);
  const [tutorialModalActive, setTutorialModalActive] = useState(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [challengeName, setChallengeName] = useState("");
  const [qrCodeLink, setQrCodeLink] = useState("");
  const [mfa, setMfa] = useState("");

  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const [successResetPassword, setSuccessResetPassword] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(null);

  const loginErrors = useSelector((store) => store.auth.loginErrors);
  const isLogout = useSelector((store) => store.auth.isLogout);
  const isMandatoryDataFetched = useSelector((store) => store.auth.isMandatoryDataFetched);

  const [loginCheckLoading, setLoginCheckLoading] = useState(false);

  const newPasswordRequired = useMemo(() => {
    return challengeName === awsAuthChallengeTypes.NEW_PASSWORD_REQUIRED;
  }, [challengeName]);

  const mfaSetup = useMemo(() => {
    return !!(challengeName === awsAuthChallengeTypes.MFA_SETUP && qrCodeLink);
  }, [challengeName, qrCodeLink]);

  const mfaInput = useMemo(() => {
    return challengeName === awsAuthChallengeTypes.SOFTWARE_TOKEN_MFA;
  }, [challengeName]);

  const simpleLogin = useMemo(() => {
    return !newPasswordRequired && !mfaSetup && !mfaInput && !forgotPassword;
  }, [newPasswordRequired, mfaSetup, mfaInput, forgotPassword]);

  const handleMfaHelpClick = () => {
    setTutorialModalActive(true);
  };

  const onMandatoryDataFetched = () => {
    dispatch(setIsMandatoryDataFetched(true));
    setIsMandatoryDataFetching(false);
  };

  const checkIfAuthorized = async () => {
    setLoginCheckLoading(true);
    try {
      const cognitoUser = await authService.getAuthorizedUser();

      if (cognitoUser) {
        dispatch(setCognitoUser(cognitoUser));
      }

      return !!cognitoUser;
    } catch (error) {
      handleError(error);

      return false;
    } finally {
      setLoginCheckLoading(false);
    }
  };

  const handleError = (error) => {
    let title = strCode.LOGIN_FAILED_ERROR;
    let message = "";

    if (error.message === awsAuthErrorTypes.USER_APP_TYPES_AVAILABILITY) {
      message = strCode.ERROR_NO_ACCESS;
    }

    if (error.code === awsAuthErrorTypes.INVALID_PARAMETER_EXCEPTION) {
      message = mfa ? strCode.ERROR_INVALID_CODE : strCode.ERROR_PASSWORD_OR_USER_NOT_EXIST;
    }

    if (error?.message === awsAuthErrorTypes.EXPIRE_SESSION) {
      message = strCode.ERROR_SESSION_EXPIRED;

      setChallengeName(null);
    }

    if (error?.code === awsAuthErrorTypes.MFA_SETUP_PARAMETER_EXCEPTION) {
      message = strCode.ERROR_MFA_SESSION_EXPIRED;
      setChallengeName(null);
    }

    if (error?.code === awsAuthErrorTypes.CODE_MISMATCH_EXCEPTION) {
      title = strCode.INVALID_VERIFICARION_CODE_ERROR;
      message = strCode.ERROR_INVALID_VERIFICARION_CODE;
    }

    if (
      error.code === awsAuthErrorTypes.NOT_AUTHORIZED_EXCEPTION ||
      error.code === awsAuthErrorTypes.USER_NOT_FOUND
    ) {
      if (
        error?.code === awsAuthErrorTypes.NOT_AUTHORIZED_EXCEPTION &&
        error?.message === awsAuthErrorTypes.EXPIRE_MFA_SESSION
      ) {
        message = strCode.ERROR_MFA_SESSION_EXPIRED;
        setChallengeName(null);
      }

      if (challengeName === awsAuthChallengeTypes.MFA_SETUP) {
        message = strCode.ERROR_MFA_SESSION_EXPIRED;
        setChallengeName(null);
      }

      if (error.code === awsAuthErrorTypes.USER_NOT_FOUND && forgotPassword) {
        title = strCode.USER_NOT_FOUND_ERROR;
        message = strCode.ERROR_PASSWORD_OR_USER_NOT_EXIST;
      } else {
        handleAccountLockoutErrors();
      }
    }

    if (
      error.code === awsAuthErrorTypes.USER_LAMBDA_VALIDATION_EXCEPTION &&
      error?.message.includes(ACCOUNT_LOCK_LAMBDA_ERROR_MESSAGE)
    ) {
      handleAccountLockoutErrors(true);
    } else if (
      error.code === awsAuthErrorTypes.USER_LAMBDA_VALIDATION_EXCEPTION &&
      error?.message.includes(awsAuthErrorTypes.USER_IS_INACTIVE)
    ) {
      title = strCode.USER_INACTIVE_ERROR_TITLE;
      message = strCode.USER_INACTIVE_ERROR_MESSAGE;
    } else {
      if (error?.title) {
        title = error.title;
      }

      message = error?.message;
    }

    if (title && message) {
      notificationService.showError({ title: t(title), message: t(message) });
      setHasError(true);
    }
  };

  const handleAccountLockoutErrors = (isAccountLocked = false) => {
    const errorTimestamp = new Date().getTime();
    const userErrors = loginErrors.find((errors) => errors.username === username);

    if (!userErrors && isAccountLocked) {
      notificationService.showError({
        message: t(strCode.ERROR_ACCOUNT_LOCKED, { minutesLeft: "a few" })
      });

      return;
    }

    if (userErrors) {
      const filteredErrorTimestamps = userErrors.errorTimestamps.filter(
        (timestamp) => timestamp + ERROR_THRESHOLD > errorTimestamp
      );

      if (filteredErrorTimestamps.length < MAX_LOGIN_ATTEMPTS - 1 && !isAccountLocked) {
        userErrors.errorTimestamps = [...filteredErrorTimestamps, errorTimestamp];

        dispatch(setUserLoginErrors(userErrors));

        notificationService.showError({ message: t(strCode.ERROR_PASSWORD_OR_USER_NOT_EXIST) });
      } else {
        const minutesSinceFirstError = errorTimestamp - userErrors.errorTimestamps[0] ?? 0;

        let minutesLeft = Math.ceil((ACCOUNT_LOCK_TIMEOUT - minutesSinceFirstError) / (60 * 1000));
        minutesLeft = minutesLeft > 0 ? minutesLeft : 1;

        notificationService.showError({
          message: t(strCode.ERROR_ACCOUNT_LOCKED, { minutesLeft })
        });
      }
    } else {
      const userErrors = {
        username,
        errorTimestamps: [errorTimestamp]
      };

      dispatch(setUserLoginErrors(userErrors));

      notificationService.showError({ message: t(strCode.ERROR_PASSWORD_OR_USER_NOT_EXIST) });
    }
  };

  const startTotpMfaSetup = async () => {
    const user = selectChallengedUser()(getState());

    const secret = await awsAmplifyService.setupTotp(user);
    const link = authService.generateOtpAuthUrl(user.username, secret);

    setQrCodeLink(link);
  };

  const onLoginClick = async () => {
    setHasError(false);
    setMfa("");
    setIsLoading(true);

    try {
      const user = await awsAmplifyService.signIn(username, password);

      if (user) {
        const isNeedToSetupTotpMfa = user.challengeName === awsAuthChallengeTypes.MFA_SETUP;

        if (isNeedToSetupTotpMfa) {
          await startTotpMfaSetup(user.challengeName);
        }

        const mfaIsNotRequired = ![
          awsAuthChallengeTypes.MFA_SETUP,
          awsAuthChallengeTypes.SOFTWARE_TOKEN_MFA
        ].includes(user.challengeName);

        if (mfaIsNotRequired) {
          const isAuthorized = await checkIfAuthorized();

          if (isAuthorized) {
            setIsLogIn(true);
          }
        }

        setChallengeName(user?.challengeName);

        setPassword("");
      } else {
        const isAuthorized = await checkIfAuthorized();

        if (isAuthorized) {
          setIsLogIn(true);
        }
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onResetPassword = async () => {
    try {
      setIsLoading(true);

      await awsAmplifyService.forgotPassword(username);

      setResetPassword(true);
      setPassword("");

      notificationService.showSuccess({
        title: t(strCode.RESET_PASSWORD_TITLE),
        message: t(strCode.RESET_PASSWORD_DESCRIPTION)
      });
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitResetPassword = async (password, newPassword, code) => {
    setIsLoading(true);

    if (password !== newPassword) {
      handleError({
        title: t(strCode.PASSWORD_NOT_EQUAL_ERROR),
        message: t(strCode.ERROR_PASSWORD_NOT_EQUAL)
      });
    } else {
      try {
        await awsAmplifyService.forgotPasswordSubmit(username, code, newPassword);
        setSuccessResetPassword(true);
      } catch (error) {
        handleError(error);
      }
    }

    setIsLoading(false);
  };

  const onValidate = async () => {
    if (challengeName === awsAuthChallengeTypes.NEW_PASSWORD_REQUIRED) {
      setIsLoading(true);

      const user = selectChallengedUser()(getState());

      try {
        await awsAmplifyService.changeRequiredPassword(user, password);
      } catch (error) {
        handleError(error);
      }

      await startTotpMfaSetup(user.challengeName);

      setChallengeName(user.challengeName);
      setPassword("");

      setIsLoading(false);
    }
  };

  const onTotpMFASetup = async () => {
    if (challengeName === awsAuthChallengeTypes.MFA_SETUP && qrCodeLink) {
      try {
        setIsLoading(true);

        const user = selectChallengedUser()(getState());

        await awsAmplifyService.verifyTotp(user, mfa);

        const isAuthorized = await checkIfAuthorized();

        log.info(`Success Cognito input mfa; username: ${username}`);

        if (isAuthorized) {
          setIsLogIn(true);
          setIsMandatoryDataFetching(true);
        } else {
          throw new CognitoInvalidParameters(awsAuthErrorTypes.MFA_SETUP_PARAMETER_EXCEPTION);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onMFA_SMS = async () => {
    try {
      setIsLoading(true);

      const user = selectChallengedUser()(getState());

      await awsAmplifyService.confirmSignIn(user, mfa, awsAuthChallengeTypes.SOFTWARE_TOKEN_MFA);

      const isAuthorized = await checkIfAuthorized();

      if (isAuthorized) {
        setIsLogIn(true);
        setIsMandatoryDataFetching(true);
      } else {
        throw new CognitoInvalidParameters(awsAuthErrorTypes.INVALID_PARAMETER_EXCEPTION);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkAuthorization = async () => {
    const isAuthorized = await checkIfAuthorized();

    const cognitoUser = selectCognitoUser()(getState());

    if (cognitoUser && isAuthorized) {
      setIsMandatoryDataFetching(true);
    }

    return isAuthorized;
  };

  const onSingleSignOn = () => {
    Auth.federatedSignIn();
  };

  const {
    fetchCallback: fetchMandatoryData,
    error: mandatoryDataError,
    isLoading: isMandatoryDataLoading,
    isSuccess: isMandatoryDataSuccess
  } = useFetchMandatoryData();

  useEffect(() => {
    if (isMandatoryDataFetching) {
      fetchMandatoryData();
    }
  }, [isMandatoryDataFetching]);

  useEffect(() => {
    if (isMandatoryDataSuccess) {
      onMandatoryDataFetched();
    }
  }, [isMandatoryDataSuccess]);

  useEffect(() => {
    checkAuthorization().then((isAuthorized) => {
      if (isAuthorized) {
        setIsLogIn(true);
      }
    });
  }, []);

  useEffect(() => {
    if (newPasswordRequired || resetPassword) {
      const { isValidPasswordSymbol, isValidPasswordBigSymbol, isValidPasswordNumber } =
        authService.validatePasswordByFields(password);

      const isValidPasswordLength = password.length <= 8;

      setNewPasswordError({
        ...(!isValidPasswordSymbol && {
          isValidPasswordSymbol: !isValidPasswordSymbol
        }),

        ...(!isValidPasswordBigSymbol && {
          isValidPasswordBigSymbol: !isValidPasswordBigSymbol
        }),

        ...(!isValidPasswordNumber && {
          isValidPasswordNumber: !isValidPasswordNumber
        }),

        ...(isValidPasswordLength && {
          isValidPasswordLength: isValidPasswordLength
        })
      });
    }
  }, [password, newPasswordRequired]);

  useEffect(() => {
    return () => {
      setHasError(false);
      setChallengeName("");
      setMfa("");
      setUsername("");
      setPassword("");
      setQrCodeLink("");

      setNewPasswordError(null);

      setIsLogIn(false);
      setIsLoading(false);
    };
  }, []);

  useEffect(() => {
    if (successResetPassword) {
      setResetPassword(false);
      setForgotPassword(false);
      setSuccessResetPassword(false);

      notificationService.showSuccess({
        title: t(strCode.PASSWORD_SET_SUCCESS),
        message: t(strCode.PASSWORD_SET_SUCCESS_MESSAGE)
      });
    }
  }, [successResetPassword]);

  useEffect(() => {
    setHasError(false);
  }, [newPasswordRequired, forgotPassword, resetPassword]);

  useEffect(() => {
    if (isLogout) {
      setChallengeName(null);
      setIsMandatoryDataFetching(false);
      dispatch(setIsLogout(false));
    }
  }, [isLogout]);

  useEffect(() => {
    if (isLogin) {
      log.info(`Success Cognito login; username: ${username}`);
      dispatch(resetUserLoginErrors(username));
    }
  }, [isLogin, username]);

  useEffect(() => {
    dispatch(setIsLoggedIn(isLogin));
  }, [isLogin]);

  if (isLogin && isMandatoryDataFetched && !newPasswordRequired) {
    const roles = appUserService.getRoles();

    return <Redirect to={appUserService.getLoginRedirectUrl(roles)} />;
  }

  if (loginCheckLoading) {
    return (
      <PageContainer justifyContent="center" width="100%">
        <Paragraph2>{t(strCode.LOADING)}</Paragraph2>
      </PageContainer>
    );
  }

  return (
    <PageContainer justifyContent="center" width="100%">
      {isMandatoryDataFetching ? (
        <DataFetching
          fetchMandatoryData={fetchMandatoryData}
          error={mandatoryDataError}
          isFetching={isMandatoryDataLoading}
        />
      ) : resetPassword ? (
        <ResetPassword
          password={password}
          setPassword={setPassword}
          newPasswordError={newPasswordError}
          isLoading={isLoading}
          hasError={hasError}
          onSubmitResetPassword={onSubmitResetPassword}
          onResendCode={onResetPassword}
        />
      ) : simpleLogin ? (
        <SimpleLogin
          username={username}
          password={password}
          setUsername={setUsername}
          setPassword={setPassword}
          isLoading={isLoading}
          hasError={hasError}
          onLogin={onLoginClick}
          onForgotPassword={() => setForgotPassword(true)}
          onSingleSignOn={COGNITO_HOSTED_UI ? onSingleSignOn : null}
        />
      ) : newPasswordRequired ? (
        <SetupNewPassword
          password={password}
          setPassword={setPassword}
          newPasswordError={newPasswordError}
          isLoading={isLoading}
          onValidate={onValidate}
        />
      ) : forgotPassword ? (
        <ForgotPassword
          username={username}
          setUsername={setUsername}
          isLoading={isLoading}
          hasError={hasError}
          onResetPassword={onResetPassword}
          onBackToLogin={() => setForgotPassword(false)}
        />
      ) : mfaSetup ? (
        <EnterMfa
          qrCodeLink={qrCodeLink}
          mfa={mfa}
          setMfa={setMfa}
          isLoading={isLoading}
          hasError={hasError}
          onContinue={onTotpMFASetup}
          onHelpClick={handleMfaHelpClick}
        />
      ) : mfaInput ? (
        <EnterMfa
          mfa={mfa}
          setMfa={setMfa}
          isLoading={isLoading}
          hasError={hasError}
          onContinue={onMFA_SMS}
          onHelpClick={handleMfaHelpClick}
        />
      ) : null}

      <TutorialModalTemplate
        id="mfa-setup-tutorial"
        active={tutorialModalActive}
        setActive={setTutorialModalActive}
        title={t(strCode.MFA_SETUP_TUTORIAL_TITLE)}
        steps={MfaSetupTutorial}
      />
    </PageContainer>
  );
};

export default withSection(LoginPage);
